import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PurchaseCreditsModal from './PurchaseCreditsModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { user, logout, openAuthModal, userCredits } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const scrollToSection = (id) => {
    if (isHomePage) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to homepage and then scroll to section
      window.location.href = `/#${id}`;
    }
  };

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
  };

  // Determine header style based on homepage and scroll position
  const headerClasses = isHomePage && !scrolled
    ? "absolute top-0 left-0 right-0 z-50 py-4 transition-all duration-300"
    : "bg-white/90 backdrop-blur-md shadow-sm py-4 sticky top-0 z-50 transition-all duration-300";

  // Determine text color based on homepage and scroll position
  const textColor = isHomePage && !scrolled ? "text-white" : "text-gray-700";
  const logoColor = isHomePage && !scrolled ? "text-white" : "text-primary";
  
  return (
    <header className={headerClasses}>
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo and Navigation grouped together */}
        <div className="flex items-center">
          <Link to="/" className={`text-2xl font-bold ${logoColor}`}>LegalGenius</Link>
          
          {/* Desktop Navigation - moved closer to logo */}
          <nav className="hidden md:flex items-center ml-10 space-x-8">
            <Link to="/" className={`${textColor} hover:text-primary transition-colors`}>Home</Link>
            <Link 
              to="/features" 
              className={`${textColor} hover:text-primary transition-colors`}
            >
              Features
            </Link>
            <Link to="/pricing" className={`${textColor} hover:text-primary transition-colors`}>Pricing</Link>
            <Link to="/templates" className={`${textColor} hover:text-primary transition-colors`}>Create Document</Link>
          </nav>
        </div>
        
        {/* Auth and Language Buttons - reordered */}
        <div className="flex items-center space-x-4">
          {user ? (
            <>
              {/* User is logged in */}
              <div className="hidden md:flex items-center space-x-4">
                {/* Credits display */}
                <button 
                  onClick={() => setIsPurchaseModalOpen(true)}
                  className={`px-3 py-1 rounded-full border ${isHomePage && !scrolled ? 'border-white/50 text-white hover:bg-white/10' : 'border-primary/50 text-primary hover:bg-primary/5'} flex items-center space-x-1 transition-colors`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">{userCredits !== null ? userCredits : '...'} credits</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </button>

                <div className="relative group">
                  <button className={`flex items-center space-x-2 ${textColor} hover:text-primary transition-colors`}>
                    <span>{user.user_metadata?.full_name || user.email}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <Link to="/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Dashboard</Link>
                    <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Profile</Link>
                    <button 
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* User is not logged in */}
              <button 
                onClick={() => openAuthModal('login')}
                className={`hidden md:inline-flex px-4 py-2 ${isHomePage && !scrolled ? 'text-white border-white/50' : 'text-primary border-primary'} border rounded-full text-sm font-medium hover:bg-primary/5 transition-colors`}
              >
                Log in
              </button>
              
              <button 
                onClick={() => openAuthModal('signup')}
                className="hidden md:inline-flex px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full text-sm font-medium hover:shadow-lg hover:shadow-indigo-500/30 transition-all hover:scale-105"
              >
                Sign up
              </button>
            </>
          )}
          
          {/* Language Selector - moved to the end */}
          <div className="hidden md:block">
            <LanguageSelector isHomePage={isHomePage && !scrolled} />
          </div>
          
          {/* Mobile menu button */}
          <button 
            className={`md:hidden ${textColor} hover:text-primary`}
            onClick={() => setMobileMenuOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-indigo-900/95 z-50 flex flex-col justify-center items-center md:hidden">
          <button 
            className="absolute top-6 right-6 text-white"
            onClick={() => setMobileMenuOpen(false)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <nav className="flex flex-col items-center space-y-6 text-xl">
            <Link 
              to="/" 
              className="text-white hover:text-blue-200 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/features" 
              className="text-white hover:text-blue-200 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Features
            </Link>
            <Link 
              to="/pricing" 
              className="text-white hover:text-blue-200 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <Link 
              to="/templates" 
              className="text-white hover:text-blue-200 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Templates
            </Link>
          </nav>
          
          <div className="mt-10 flex flex-col space-y-4">
            {user ? (
              <>
                {/* Show credits in mobile menu too */}
                <button 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setIsPurchaseModalOpen(true);
                  }}
                  className="px-8 py-3 text-white border border-white/50 rounded-full text-lg font-medium w-48 text-center flex items-center justify-center space-x-2 hover:bg-white/10 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{userCredits !== null ? userCredits : '...'} credits</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </button>
                
                <Link 
                  to="/dashboard" 
                  className="px-8 py-3 text-white border border-white/50 rounded-full text-lg font-medium hover:bg-white/10 transition-colors w-48 text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/profile" 
                  className="px-8 py-3 text-white border border-white/50 rounded-full text-lg font-medium hover:bg-white/10 transition-colors w-48 text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                <button 
                  onClick={handleLogout}
                  className="px-8 py-3 bg-white text-indigo-600 rounded-full text-lg font-medium hover:shadow-lg transition-all w-48 text-center"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <button 
                  onClick={() => {
                    openAuthModal('login');
                    setMobileMenuOpen(false);
                  }}
                  className="px-8 py-3 text-white border border-white/50 rounded-full text-lg font-medium hover:bg-white/10 transition-colors w-48 text-center"
                >
                  Log in
                </button>
                <button 
                  onClick={() => {
                    openAuthModal('signup');
                    setMobileMenuOpen(false);
                  }}
                  className="px-8 py-3 bg-white text-indigo-600 rounded-full text-lg font-medium hover:shadow-lg transition-all w-48 text-center"
                >
                  Sign up
                </button>
              </>
            )}
            <div className="mt-4">
              <LanguageSelector isHomePage={true} />
            </div>
          </div>
        </div>
      )}

      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal 
        isOpen={isPurchaseModalOpen} 
        onClose={() => setIsPurchaseModalOpen(false)} 
      />
    </header>
  );
};

export default Header;
